import { apiService } from '../../services/api';
import { getBackendUrl } from '../../utils/https';
import { tokenStorage } from '../../utils/auth';
import {
  AskRequest,
  AssessInfluenceRequest,
  BriefRequest,
  DiscoverRequest,
  IntelligenceEvent,
  Lead,
  LeadStatus,
  NetworkGraph,
} from './types';

export class IntelligenceUnavailableError extends Error {
  constructor(message = 'Intelligence services are not available on this server yet.') {
    super(message);
    this.name = 'IntelligenceUnavailableError';
  }
}

export async function fetchNetwork(options: { includePast?: boolean } = {}): Promise<NetworkGraph> {
  try {
    const res = await apiService.get<NetworkGraph>('/api/intelligence/network', {
      params: { includePast: options.includePast ? 'true' : undefined },
    });
    return res.data;
  } catch (error: any) {
    if (error?.response?.status === 404) throw new IntelligenceUnavailableError();
    throw error;
  }
}

export async function listLeads(params: { status?: LeadStatus; kind?: Lead['kind']; subjectId?: string } = {}): Promise<Lead[]> {
  const res = await apiService.get<Lead[]>('/api/intelligence/leads', { params });
  return res.data;
}

export async function acceptLead(id: string): Promise<Lead> {
  const res = await apiService.post<Lead>(`/api/intelligence/leads/${id}/accept`, {});
  return res.data;
}

export async function dismissLead(id: string, reason?: string): Promise<Lead> {
  const res = await apiService.post<Lead>(`/api/intelligence/leads/${id}/dismiss`, { reason });
  return res.data;
}

export interface StreamHandlers {
  onEvent: (event: IntelligenceEvent) => void;
  signal?: AbortSignal;
}

/**
 * POSTs to any SSE endpoint on this backend and dispatches parsed events until the
 * stream ends. Resolves when finished; rejects on HTTP errors (IntelligenceUnavailableError
 * for 404, which means the endpoint does not exist on this server yet).
 *
 * `path` is absolute from the API root, e.g. `/api/monitoring/digest`.
 */
export async function streamSse(path: string, body: unknown, { onEvent, signal }: StreamHandlers): Promise<void> {
  const token = tokenStorage.get();
  const response = await fetch(`${getBackendUrl()}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    signal,
  });

  if (response.status === 404) throw new IntelligenceUnavailableError();
  if (!response.ok || !response.body) {
    let message = `Request failed (${response.status})`;
    try {
      const data = await response.json();
      message = data?.message ? (Array.isArray(data.message) ? data.message.join(', ') : data.message) : message;
    } catch {
      // not JSON
    }
    throw new Error(message);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const flush = (block: string) => {
    const data = block
      .split('\n')
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(line.startsWith('data: ') ? 6 : 5))
      .join('\n');
    if (!data) return;
    try {
      onEvent(JSON.parse(data) as IntelligenceEvent);
    } catch {
      // ignore malformed keep-alive lines
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep = buffer.indexOf('\n\n');
    while (sep !== -1) {
      flush(buffer.slice(0, sep));
      buffer = buffer.slice(sep + 2);
      sep = buffer.indexOf('\n\n');
    }
  }
  if (buffer.trim()) flush(buffer);
}

/** SSE endpoints under `/api/intelligence` */
export const streamIntelligence = (path: string, body: unknown, handlers: StreamHandlers): Promise<void> =>
  streamSse(`/api/intelligence/${path}`, body, handlers);

export const askIntelligence = (request: AskRequest, handlers: StreamHandlers) =>
  streamIntelligence('ask', request, handlers);

export const briefIntelligence = (request: BriefRequest, handlers: StreamHandlers) =>
  streamIntelligence('brief', request, handlers);

export const discoverConnections = (request: DiscoverRequest, handlers: StreamHandlers) =>
  streamIntelligence('discover', request, handlers);

export const assessInfluence = (request: AssessInfluenceRequest, handlers: StreamHandlers) =>
  streamIntelligence('assess-influence', request, handlers);
